import { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { AccountPageHeader } from "../components/AccountPageHeader";
import { getDateString } from "../css/Utility";
import { getUserTickets } from "../services/TicketService";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { EmptyState } from "../components/EmptyState";
import { TicketDetailedFill, PlusLg } from "react-bootstrap-icons";

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { key: 'open',   label: 'Open'   },
  { key: 'closed', label: 'Closed' },
  { key: 'all',    label: 'All'    },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function TicketStatusBadge({ status }) {
  const closed = (status || '').toLowerCase() === 'closed';
  return (
    <span className={`tl-badge${closed ? ' tl-badge--closed' : ' tl-badge--open'}`}>
      {status}
    </span>
  );
}

function TicketCard({ ticket, onClick }) {
  return (
    <div
      className="tl-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick()}
    >
      <div className="tl-card-top">
        <p className="tl-card-subject">{ticket.subject}</p>
        <TicketStatusBadge status={ticket.status} />
      </div>
      <p className="tl-card-meta">
        <span>#{ticket.id}</span>
        <span className="tl-card-sep" aria-hidden="true">·</span>
        <span>Opened {getDateString(ticket.timestamp)}</span>
      </p>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function TicketPage() {
  const [tickets,      setTickets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const history = useHistory();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getUserTickets();
        if (!cancelled) setTickets(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    if (statusFilter === 'open')   return tickets.filter(t => t.status !== 'Closed');
    if (statusFilter === 'closed') return tickets.filter(t => t.status === 'Closed');
    return tickets;
  }, [tickets, statusFilter]);

  if (loading) return <LoadingSpinner />;
  if (error)   return <p className="tl-error">Failed to load tickets: {error}</p>;

  return (
    <div className="tl-page">
      <AccountPageHeader
        icon={<TicketDetailedFill />}
        title="Support Tickets"
        description="View and manage your support requests"
        actions={
          <Link to="/createticket" className="tl-btn-new">
            <PlusLg aria-hidden="true" /> New Ticket
          </Link>
        }
      />

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      {tickets.length === 0 ? (
        <EmptyState
          message="You haven't submitted any tickets yet."
          ctaLabel="Open a Ticket"
          ctaTo="/createticket"
        />
      ) : (
        <>
          <div className="tl-filters">
            <div className="tl-filter-group">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  className={`tl-filter-btn${statusFilter === opt.key ? ' tl-filter-btn--active' : ''}`}
                  onClick={() => setStatusFilter(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="tl-count">
              {visible.length} ticket{visible.length !== 1 ? 's' : ''}
            </span>
          </div>

          {visible.length === 0 ? (
            <EmptyState message="No tickets match the selected filter." />
          ) : (
            <div className="tl-list">
              {visible.map(t => (
                <TicketCard
                  key={t.id}
                  ticket={t}
                  onClick={() => history.push('/ticket/' + t.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export { TicketPage };
